import { memo, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Stack, toast } from "@altimateai/lego";
import DataTable from "@/components/tables/DataTable";
import { Paginate } from "@/components/paginate";
import { useAppState } from "@/modules/app/useAppContext";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { getOpportunitiesRedirection } from "@/helpers/apis";
import { getResourceOpportunityUrl } from "@/pages/query/components";
import { useExplorerFiltersFromUrl } from "../../hooks";
import type { GetOpportunitiesDataResponse, Page, OwnerListItem, SelectedRow } from "../../types";
import { getEffortLabel, getOpportunityStatus } from "../../utils";
import OwnerAssignerDropdown from "../ownerAssignerDropdown/OwnerAssignerDropdown";
import EffortTag from "../effortTag/EffortTag";
import StatusTag from "../statusTag/StatusTag";
import { DetailsCell, ResourceCell, MoneySavingsCell, TimeSavingsCell } from "./cells";
import { TableHeaderType } from "@/components/tables/types";

interface OpportunitiesTableProps {
  data: Page<GetOpportunitiesDataResponse>;
  ownerList: OwnerListItem[] | null;
  onSelectionChanged: (rows: SelectedRow[]) => void;
  onOwnerChange: (owner: number | string, opportunity_id: string) => void;
}

export const OpportunitiesTable = memo(
  ({ data, ownerList, onSelectionChanged, onOwnerChange }: OpportunitiesTableProps) => {
    const { startDate, endDate, page, pageSize } = useExplorerFiltersFromUrl();
    const { datastores = [] } = useAppState();

    const { updateSearchParamsByKey } = useFiltersBySearchParams();
    const navigate = useNavigate();
    const { mutate } = useMutation({
      mutationFn: getOpportunitiesRedirection,
      onSuccess: (redirectData, variables) => {
        if (!redirectData?.url) {
          toast({
            variant: "destructive",
            title: "Unable to redirect",
            description: "No URL returned for this opportunity",
          });
          return;
        }
        navigate(
          getResourceOpportunityUrl({
            resource_type: variables.resource_type,
            opportunity_id: variables.resource_id ?? "",
            url: redirectData.url,
            startDate,
            endDate,
          })
        );
      },
      onError: () => {
        toast({
          variant: "destructive",
          title: "Redirection failed",
          description: "Failed to fetch opportunity redirection",
        });
      },
    });

    const navigateToOpportunity = (row: GetOpportunitiesDataResponse) => {
      mutate({
        resource_type: row.resource_type,
        resource_uri: row.resources?.[0]?.resource_uri || null,
        resource_id: row.opportunity_id,
        resource_name: row.resource_name || null,
        instance_id: row.instance_id.toString(),
        opportunity_id: row.opportunity_id,
      });
    };

    const handlePageChange = (newPage: number) => {
      updateSearchParamsByKey({ page: (newPage + 1).toString() });
    };

    const handlePageSizeChange = (newPageSize: number) => {
      updateSearchParamsByKey({ size: newPageSize.toString(), page: "1" });
    };

    const handleRowSelect = useCallback(
      (rows: GetOpportunitiesDataResponse[]) => {
        onSelectionChanged(rows.map(row => ({ opportunity_id: row.opportunity_id })));
      },
      [onSelectionChanged]
    );

    const headers: TableHeaderType<GetOpportunitiesDataResponse>[] = useMemo(
      () => [
        {
          id: 0,
          label: "Details",
          uid: "details",
          width: 350,
          render: (item: GetOpportunitiesDataResponse) => (
            <DetailsCell
              resource_name={item.resource_name}
              resource_type={item.resource_type}
              resource_uri={item.resources?.[0]?.resource_uri || ""}
              opportunity_name={item.opportunity_name}
              description={item.description || ""}
              last_seen={item.last_seen}
              first_seen={item.first_seen}
              count={item.count}
              total_count={item.total_count || 0}
            />
          ),
        },
        {
          id: 1,
          label: "Resources",
          uid: "resources",
          width: 220,
          render: (item: GetOpportunitiesDataResponse) => (
            <ResourceCell resources={item.resources} />
          ),
        },
        {
          id: 2,
          label: "Money Savings",
          uid: "money_savings",
          width: 140,
          render: (item: GetOpportunitiesDataResponse) => (
            <MoneySavingsCell
              savings={{
                min: parseFloat(item.money_savings.min),
                max: parseFloat(item.money_savings.max),
              }}
            />
          ),
          tooltipInfo:
            "Possible money savings during the selected time period",
          sort: {
            sortSearchKey: "sort",
            sortOrderSearchKey: "sort_order",
            sortParamValue: "cost_savings_max",
          },
        },
        {
          id: 3,
          label: "Time Savings",
          uid: "time_savings",
          width: 140,
          render: (item: GetOpportunitiesDataResponse) => (
            <TimeSavingsCell time={item.time_savings} />
          ),
          tooltipInfo:
            "Possible time savings during the selected time period",
          sort: {
            sortSearchKey: "sort",
            sortOrderSearchKey: "sort_order",
            sortParamValue: "time_savings_max",
          },
        },
        {
          id: 4,
          label: "Effort",
          uid: "effort",
          width: 100,
          render: (item: GetOpportunitiesDataResponse) => (
            <EffortTag effort={getEffortLabel(item.effort)} />
          ),
        },
        {
          id: 5,
          label: "Owner",
          uid: "owner",
          width: 140,
          render: (item: GetOpportunitiesDataResponse) => (
            <OwnerAssignerDropdown
              assignedOwner={ownerList
                ?.find(o => o.opportunity_id === item.opportunity_id)
                ?.owner?.toString()}
              setAssignedOwner={(owner: number | string) => {
                onOwnerChange(owner, item.opportunity_id);
              }}
              opportunity_id={item.opportunity_id}
              startDate={startDate?.toISOString()}
              endDate={endDate?.toISOString()}
            />
          ),
        },
        {
          id: 6,
          label: "Status",
          uid: "status",
          width: 120,
          tooltipInfo: (
            <Stack direction={"column"} gap={"md"}>
              <div>
                Active: This opportunity has been discovered but not implemented or dismissed yet.
              </div>
              <div>Implemented: This opportunity has been implemented already</div>
              <div>
                Dismissed: This opportunity cannot be implemented and it has been dismissed.
              </div>
            </Stack>
          ),
          render: (item: GetOpportunitiesDataResponse) => (
            <StatusTag status={getOpportunityStatus(item.status)} />
          ),
        },
        {
          id: 7,
          label: "Instance",
          uid: "instance",
          width: 200,
          render: (item: GetOpportunitiesDataResponse) => (
            <div>{datastores?.find(d => Number(d.instance_id) === item.instance_id)?.name}</div>
          ),
        },
      ],
      [ownerList, datastores, startDate, endDate, onOwnerChange]
    );

    return (
      <>
        <DataTable<GetOpportunitiesDataResponse>
          enableRowSelection
          onRowSelect={handleRowSelect}
          onRowClick={row => navigateToOpportunity(row)}
          header={headers}
          data={{ items: data.items }}
          columnPickerPortal="opportunities-column-picker"
          defaultSort={{ sortParamValue: "cost_savings_max", order: "desc" }}
        />
        <Paginate
          itemCount={data.total}
          numPages={data.pages}
          page={page}
          pageSize={pageSize}
          onPageClick={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showPageSize
        />
      </>
    );
  }
);

OpportunitiesTable.displayName = "OpportunitiesTable";
